import { RecursiveItemTraceabilityDTO } from '../../pages/OriginItems/origin-items.types'
import { IError } from '../base/error'
import ServiceBase from '../base/service.base'

import {
  IOrderTraceability,
  IInvoiceTraceability,
  IOrderInvoiceTraceabilityStatistics,
  IItemTraceability,
  IBrandsTraceabilityStatistics,
  IOrderTraceabilityStatistics,
  IItemsStatisticsDTO,
  OrderRawStatisticsDataDTO,
  OrderInvoiceRawStatisticsDataDTO,
  ItemRawStatisticsDataDTO,
  BrandRawStatisticsDataDTO,
  IItemShortTreeTraceability,
  IDashboardStatistics,
} from './traceability.types'

export class Traceability extends ServiceBase {
  private traceabilityMainUrl = '/traceability'
  private statisticsMainUrl = '/traceability/statistics'
  private statisticsDataMainUrl = '/traceability/statistics/data'

  /**
   * Get traceability from an order
   *
   * @param invoiceId An **invoice id**, from an invoice that is an order invoice supply
   * @param jwt user jwt
   * @returns order traceability
   */
  public async order(invoiceId: string): Promise<IOrderTraceability[]> {
    const url = `${this.traceabilityMainUrl}/orders/${invoiceId}`

    const response = await this.request.get<IOrderTraceability[], IError>(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async orderStatistics(
    orderCode: string,
  ): Promise<IOrderTraceabilityStatistics> {
    const url = `${this.statisticsMainUrl}/order/${orderCode}`

    const response = await this.request.get<
      IOrderTraceabilityStatistics,
      IError
    >(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async orderStatisticsData(
    orderCode: string,
  ): Promise<OrderRawStatisticsDataDTO> {
    const url = `${this.statisticsDataMainUrl}/order/${orderCode}`

    const response = await this.request.get<OrderRawStatisticsDataDTO, IError>(
      url,
    )

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  /**
   * Get traceability from an order invoice
   *
   * @param orderCode order code
   * @param invoiceCode invoice code
   * @param jwt user jwt
   * @returns invoice traceability
   */
  public async orderInvoice(
    orderCode: string,
    invoiceCode: string,
  ): Promise<IInvoiceTraceability> {
    const url = `${this.traceabilityMainUrl}/orderinvoice/${orderCode}/${invoiceCode}`

    const response = await this.request.get<IInvoiceTraceability, IError>(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  /**
   * Get statistics from an order invoice
   *
   * @param invoiceId invoice id
   * @param jwt user jwt
   * @returns order invoice statistics
   */
  public async orderInvoiceStatistics(
    invoiceId: string,
  ): Promise<IOrderInvoiceTraceabilityStatistics> {
    const url = `${this.statisticsMainUrl}/orderinvoice/${invoiceId}`

    const response = await this.request.get<
      IOrderInvoiceTraceabilityStatistics,
      IError
    >(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async orderInvoiceStatisticsData(
    invoiceId: string,
  ): Promise<OrderInvoiceRawStatisticsDataDTO> {
    const url = `${this.statisticsDataMainUrl}/orderinvoice/${invoiceId}`

    const response = await this.request.get<
      OrderInvoiceRawStatisticsDataDTO,
      IError
    >(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  /**
   * Get traceability of an item
   *
   * @param itemId item id
   * @param jwt user jwt
   * @returns item traceability
   */
  public async item(itemId: string): Promise<IItemTraceability[]> {
    const url = `${this.traceabilityMainUrl}/item/${itemId}`

    const response = await this.request.get<IItemTraceability[], IError>(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  /**
   * Get traceability of an item
   *
   * @param itemId item id
   * @param jwt user jwt
   * @returns item traceability
   */
  public async recursiveItem(
    itemId: string,
  ): Promise<RecursiveItemTraceabilityDTO> {
    const url = `${this.traceabilityMainUrl}/recursive/item/${itemId}`

    const response = await this.request.get<
      RecursiveItemTraceabilityDTO,
      IError
    >(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async itemStatistics(itemId: string): Promise<IItemsStatisticsDTO> {
    const url = `${this.statisticsMainUrl}/item/${itemId}`

    const response = await this.request.get<IItemsStatisticsDTO, IError>(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async itemStatisticsData(
    itemId: string,
  ): Promise<ItemRawStatisticsDataDTO> {
    const url = `${this.statisticsDataMainUrl}/item/${itemId}`

    const response = await this.request.get<ItemRawStatisticsDataDTO, IError>(
      url,
    )

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  /**
   * Get traceability statistics of multiple brands
   *
   */
  public async brandsStatistics(
    startDate?: string,
    endDate?: string,
  ): Promise<IBrandsTraceabilityStatistics> {
    const url = `${this.statisticsMainUrl}/brand`

    const response = await this.request.get<
      IBrandsTraceabilityStatistics,
      IError
    >(url, {
      params: {
        from: startDate || undefined,
        to: endDate || undefined,
      },
    })

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async dashboardStatistics(): Promise<IDashboardStatistics> {
    const url = `${this.statisticsMainUrl}/dashboard`

    const response = await this.request.get<IDashboardStatistics, IError>(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  /**
   * @deprecated Not being used
   */
  public async brandsStatisticsData(): Promise<BrandRawStatisticsDataDTO> {
    const url = `${this.statisticsDataMainUrl}/brand`

    const response = await this.request.get<BrandRawStatisticsDataDTO, IError>(
      url,
    )

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async blockchainTreeShort(
    itemId: string,
  ): Promise<IItemShortTreeTraceability> {
    const url = `${this.traceabilityMainUrl}/tree/blockchain-info/item/${itemId}`

    const response = await this.request.get<IItemShortTreeTraceability, IError>(
      url,
    )

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async companyStatistics(taxId: string): Promise<IItemsStatisticsDTO> {
    const url = `${this.statisticsMainUrl}/company/${taxId}`

    const response = await this.request.get<IItemsStatisticsDTO, IError>(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async companiesStatistics(): Promise<IItemsStatisticsDTO> {
    const url = `${this.statisticsMainUrl}/company-providers/alltiers/list`

    const response = await this.request.get<IItemsStatisticsDTO, IError>(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }
}
